import fs from "node:fs";
import path from "node:path";
import { listMyOrgs, type Org } from "../lib/api.js";
import { loadAuth } from "../lib/auth.js";

interface WorkspaceInitOptions {
  org?: string;
  name?: string;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 30);
}

/**
 * Resolve which org the workspace binds to: an explicit --org wins, otherwise
 * the active org, otherwise the sole org if the user only belongs to one.
 * Errors (rather than guessing) when the choice is ambiguous.
 */
async function resolveOrg(opts?: WorkspaceInitOptions): Promise<Org> {
  const orgs = await listMyOrgs();
  if (orgs.length === 0) {
    console.error("No organizations on your account. Run `clawnify login` first.");
    process.exit(1);
  }

  if (opts?.org) {
    const match = orgs.find((o) => o.id === opts.org || o.slug === opts.org);
    if (!match) {
      console.error(
        `No org matched "${opts.org}". Available: ${orgs.map((o) => o.slug).join(", ")}`,
      );
      process.exit(1);
    }
    return match;
  }

  const auth = loadAuth();
  const active = orgs.find((o) => o.id === auth?.org_id);
  if (active) return active;
  if (orgs.length === 1) return orgs[0]!;

  console.error(
    "Multiple orgs — pick one with `--org <slug>` (or set a default: `clawnify org use <slug>`).\n" +
    `  Available: ${orgs.map((o) => o.slug).join(", ")}`,
  );
  process.exit(1);
}

function write(dir: string, file: string, content: string) {
  const fullPath = path.join(dir, file);
  fs.mkdirSync(path.dirname(fullPath), { recursive: true });
  fs.writeFileSync(fullPath, content);
}

export async function workspaceInitCommand(
  dir?: string,
  opts?: WorkspaceInitOptions,
): Promise<void> {
  const org = await resolveOrg(opts);
  const name = opts?.name || org.name;
  const targetDir = dir ? path.resolve(dir) : path.resolve(slugify(org.slug));

  if (fs.existsSync(targetDir) && fs.readdirSync(targetDir).length > 0) {
    console.error(`Directory ${targetDir} is not empty.`);
    process.exit(1);
  }

  // clawnify.json — workspace root. No app/website block; binds the org.
  // The org is stored as a slug (human-readable, matches `--org` semantics);
  // deploy resolves it to an id at push time.
  write(targetDir, "clawnify.json", JSON.stringify({
    $schema: "https://app.clawnify.com/schema/v1/clawnify.json",
    version: 1,
    name,
    workspace: { org: org.slug },
  }, null, 2));

  // pnpm workspace so one `pnpm install` hoists deps across every app, plus
  // the org's single website.
  write(targetDir, "pnpm-workspace.yaml", `packages:
  - "apps/*"
  - "website"
`);

  write(targetDir, "package.json", JSON.stringify({
    name: slugify(org.slug),
    private: true,
    type: "module",
  }, null, 2));

  // Root .gitignore — per-app link state (.clawnify) and secrets stay local;
  // the org binding in clawnify.json is the only thing committed for routing.
  write(targetDir, ".gitignore", `node_modules
dist
.wrangler
.clawnify
.env*
!.env.example
.dev.vars
data.db
data.db-wal
data.db-shm
`);

  write(targetDir, "README.md", `# ${name}

Clawnify workspace for the **${org.name}** org (\`${org.slug}\`).

Layout:

- \`apps/<name>/\` — one folder per app; each deploys to this org automatically.
- \`agents/<name>/\` — one folder per OpenClaw agent (AGENT.md, skills/, flows/).
- \`website/\` — the org's single public website (Astro).

## Add an app / an agent / the website

\`\`\`bash
clawnify init                 # app   → apps/<name>/ (inherits the org)
clawnify init --type agent    # agent → agents/<name>/
clawnify init --type website  # site  → website/
\`\`\`

## Deploy

\`\`\`bash
clawnify deploy apps/<name>   # one app   (or: cd apps/<name> && clawnify deploy)
clawnify deploy website       # the website
\`\`\`

## Install dependencies

\`\`\`bash
pnpm install                  # hoists deps for every app + the website
\`\`\`

## GitHub sync (optional)

Push this repo to a single GitHub monorepo, then in the dashboard link each app
and the website to its own subdirectory (\`apps/<name>\`, \`website\`). Pushing the
repo auto-deploys only the targets whose folder changed; Clawnify mirrors edits
back under the same subdirs. See \`docs/internal/adr-company-monorepo.md\` § "GitHub sync".
`);

  // Keep apps/, agents/, and website/ in git even before anything is added.
  write(targetDir, "apps/.gitkeep", "");
  write(targetDir, "agents/.gitkeep", "");
  write(targetDir, "website/.gitkeep", "");

  const rel = path.relative(process.cwd(), targetDir) || ".";
  console.log();
  console.log(`  Created workspace for ${org.name} (${org.slug}) in ${rel}/`);
  console.log();
  console.log("  Get started:");
  if (rel !== ".") console.log(`    cd ${rel}`);
  console.log(`    clawnify init          # add an app under apps/`);
  console.log(`    clawnify deploy apps/<name>`);
  console.log();
}
